import { forwardRef, InputHTMLAttributes, ReactElement, useState } from "react";
import { cn } from "@repo/ui/lib/utils";
import Icon from "@repo/ui/components/ui/icon/icon";

interface FloatingLabelInputProps
  extends InputHTMLAttributes<HTMLInputElement> {
  id?: string;
  label: string | ReactElement;
  type?: string;
  value?: string | number;
  ref?: any;
  onBlur?: () => void;
}

const FloatingLabelInput: React.FC<FloatingLabelInputProps> = forwardRef<
  HTMLInputElement,
  FloatingLabelInputProps
>(({ id, label, type = "text", value, onBlur, required, ...props }, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [pwdRevealed, setPwdRevealed] = useState(false);
  const togglePwd = () => setPwdRevealed(!pwdRevealed);
  const isSafari =
    typeof window !== "undefined" &&
    /^((?!chrome|android).)*safari/i.test(navigator?.userAgent);

  return (
    <div className={cn("relative w-full flex justify-end items-center")}>
      <input
        id={id}
        type={pwdRevealed ? "text" : type}
        value={value}
        ref={ref}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          if (onBlur) {
            onBlur();
          }
          setIsFocused(false);
        }}
        className={cn(
          "block rounded-[10px] w-full h-[70px] px-[20px] pt-3 text-[16px] font-black text-gray-900 bg-secondary-gray1 dark:bg-secondary-black border-separate focus:bg-secondary-gray2 dark:focus:bg-primary-white focus:outline-none focus:ring-1 focus:ring-secondary-black peer dark:text-primary-black",
          "peer autofill:shadow-[inset_0_0_0px_1000px_#F6F4F6] dark:autofill:shadow-[inset_0_0_0px_1000px_#FFFFFF] dark:autofill:dark:text-primary-black",
          { "safari-input-date": type === "date" && isSafari },
          { "safari-input-pwd": type === "password" && isSafari },
          {
            "ring-0 bg-secondary-gray2 dark:bg-primary-white":
              (value || props.defaultValue) && !isFocused,
          },
          {
            "safari-hide-placeholder":
              isSafari && !value && !props.defaultValue && !isFocused,
          },
          { "cursor-not-allowed opacity-60": props.disabled },
        )}
        placeholder=" "
        required={required}
        {...props}
      />
      <label
        htmlFor={id}
        className={cn(
          "absolute left-[20px] top-[26px] text-sm text-secondary-black dark:text-primary-white font-black duration-150 transform scale-100 origin-[0] peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:scale-75 peer-focus:-translate-y-3",
          "peer-autofill:scale-75 peer-autofill:-translate-y-3 peer-autofill:font-medium dark:peer-autofill:text-primary-black",
          {
            "scale-75 -translate-y-3 font-medium dark:text-primary-black":
              value || props.defaultValue || isFocused,
          },
        )}
      >
        {label} {required ? "*" : ""}
      </label>
      {(() => {
        if (type === "password") {
          return (
            <Icon
              icon={pwdRevealed ? "EyeOpen" : "EyeClosed"}
              className={cn(
                "cursor-pointer stroke-primary-magenta fill-none h-[24px] w-[30px] absolute right-[20px] z-1",
                { hidden: type !== "password" },
              )}
              onClick={togglePwd}
            />
          );
        }
      })()}
    </div>
  );
});

export { FloatingLabelInput };
